import { AfterViewInit, Component, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Meta } from '@angular/platform-browser';
import { FeaturesService } from '@libraries/feature-flags';
import { AddressAutocompleteComponent } from '@libraries/google-maps';
import { emailPattern, validateAddress, ValidateEmail } from '@libraries/helpers';
import { ProjectsService } from '@libraries/manager-client';
import { phoneMask, phoneNumberValidator } from '@libraries/ui';
import { isMobile } from '@libraries/ui-state';
import { select, Store } from '@ngrx/store';
import loadjs from 'loadjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { i18nImprovements, i18nTimeFrames } from '../../helpers';
import {
  CreateProject,
  getAddressError,
  getEligibilityImage,
  getQuestionsPrefill,
  isLeftOutletLoading,
  selectQueryParams,
} from '../../state';

@Component({
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss'],
  standalone: false,
})
export class QuestionsComponent implements OnInit, AfterViewInit {
  // Local Imports
  public phoneFormat = phoneMask;
  public baseQuestions: UntypedFormGroup;
  public pageForm: UntypedFormGroup;
  public promocode: string = null;

  // Observables
  public isMobile$ = this.store.pipe(select(isMobile));
  public isLoading$ = this.store.pipe(select(isLeftOutletLoading));
  public addressError$ = this.store.pipe(select(getAddressError));
  public eligibilityImage$ = this.store.select(getEligibilityImage);
  public getQueryParams$ = this.store.pipe(select(selectQueryParams));

  // User Selection Options
  public improvements = i18nImprovements;
  public timeframes = i18nTimeFrames;

  unsecuredProductPhase2PoFeatureFlag = this.features.isOn('yt-18411-unsecured-product-phase-2-po');

  @ViewChildren(MatFormFieldControl)
  inputs: MatFormFieldControl<MatInput>[] = [];

  @ViewChild(AddressAutocompleteComponent, { static: false })
  autocomplete: AddressAutocompleteComponent;
  public environmentName = environment.envName;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private service: ProjectsService,
    public features: FeaturesService,
    private meta: Meta
  ) {
    this.buildForm();
    this.prefillForm();
  }

  // LifeCycle Hooks

  ngOnInit() {
    if (this.environmentName == 'production') {
      this.meta.removeTag("name='robots'");
    }
    this.addressError$.subscribe((result) => {
      if (result) {
        this.baseQuestions.controls['address'].setErrors({ invalid: true });
      }
    });
    // Set params
    this.getQueryParams$.subscribe((params) => {
      this.promocode = params.promocode;
    });
    loadjs(environment.ambassadorReferralGetCookieUrl, {
      returnPromise: true,
    }).catch((path) => {
      throw new Error(`Failed to load Ambassador Referral GetCookie Url ${path}`);
    });
  }

  ngAfterViewInit() {
    setTimeout(() => this.autocomplete.field.focus(), 1000);
  }

  // Setters & Getters //

  get inputFocused() {
    return this.inputs.some((input) => input.focused);
  }

  get redirectUrl() {
    return environment.redirectUrl;
  }

  // Functions //

  public submit() {
    this.pageForm.markAllAsTouched();
    if (this.pageForm.valid) {
      const payload = this.promocode
        ? { ...this.pageForm.value, promocode: this.promocode }
        : { ...this.pageForm.value };
      payload.improvements = payload.improvements.filter(
        (eachImprovement) => eachImprovement !== 'Other'
      );
      this.store.dispatch(CreateProject({ payload: payload }));
    }
  }

  // Form Builder

  prefillForm() {
    this.store.pipe(select(getQuestionsPrefill), take(1)).subscribe((questionsData) => {
      this.pageForm.patchValue({
        ...questionsData,
      });
    });
  }

  private buildForm() {
    this.baseQuestions = this.fb.group({
      address: [null, Validators.compose([Validators.required, validateAddress])],
      email: [
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.pattern(emailPattern),
        ]),
        ValidateEmail.create(this.service, 200, true),
      ],
      phone: [
        '',
        Validators.compose([Validators.required, Validators.minLength(2), phoneNumberValidator]),
      ],
      email_communication: true,
    });

    // Aggregate
    this.pageForm = this.fb.group({
      baseQuestions: this.baseQuestions,
      improvements: [[]],
      timeframes: [],
    });
  }
}
